import React, { useState, useRef, useEffect } from 'react';
import { FaUndo, FaCheck, FaPlay, FaPause, FaVolumeUp } from 'react-icons/fa';
import { persianLetterShapes, LetterShape } from '../data/persianLetterShapes';
import './AlphabetTracing.css';

interface AlphabetTracingProps {
  letter?: string;
  onComplete?: (letter: string, accuracy: number) => void;
  onAudioPlay?: (audioUrl: string) => void;
}

interface TracingPoint {
  x: number;
  y: number;
  timestamp: number;
}

const AlphabetTracing: React.FC<AlphabetTracingProps> = ({
  letter = 'آ',
  onComplete,
  onAudioPlay
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentLetter, setCurrentLetter] = useState<LetterShape | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tracingPoints, setTracingPoints] = useState<TracingPoint[]>([]);
  const [currentStroke, setCurrentStroke] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [accuracy, setAccuracy] = useState(0);
  const [showGuide, setShowGuide] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentForm, setCurrentForm] = useState<'isolated' | 'initial' | 'medial' | 'final'>('isolated');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0);

  // Initialize letter shape
  useEffect(() => {
    const letterShape = persianLetterShapes.find(shape => shape.letter === letter);
    
    if (letterShape) {
      setCurrentLetter(letterShape);
      resetTracing();
    } else {
      // Fallback to first letter if current letter not found
      if (persianLetterShapes.length > 0) {
        setCurrentLetter(persianLetterShapes[0]);
        resetTracing();
      }
    }
  }, [letter]);

  // Redraw guide lines when showGuide changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && currentLetter) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear and redraw everything
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawGuideLines(ctx);
        
        // Redraw user's tracing if any
        if (tracingPoints.length > 0) {
          ctx.save();
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          ctx.beginPath();
          ctx.moveTo(tracingPoints[0].x, tracingPoints[0].y);
          for (let i = 1; i < tracingPoints.length; i++) {
            ctx.lineTo(tracingPoints[i].x, tracingPoints[i].y);
          }
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }, [showGuide, currentLetter, currentForm]);

  // Force redraw when form changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && currentLetter) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Redraw guide with new form
        drawGuideLines(ctx);
        
        // Redraw user tracing if any
        if (tracingPoints.length > 0) {
          ctx.save();
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          ctx.beginPath();
          ctx.moveTo(tracingPoints[0].x, tracingPoints[0].y);
          for (let i = 1; i < tracingPoints.length; i++) {
            ctx.lineTo(tracingPoints[i].x, tracingPoints[i].y);
          }
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }, [currentForm]);

  const resetTracing = () => {
    setTracingPoints([]);
    setCurrentStroke(0);
    setIsCompleted(false);
    setAccuracy(0);
    setIsPlaying(false);
    setShowFeedback(false);
    setCurrentForm('isolated');
    setIsAnimating(false);
    setAnimationStep(0);
    setAnimationProgress(0);
    
    // Clear canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawGuideLines(ctx);
      }
    }
  };

  const startAnimation = () => {
    if (!currentLetter) return;
    
    console.log('Starting animation for letter:', currentLetter.letter);
    console.log('Stroke order:', currentLetter.strokeOrder);
    
    setIsAnimating(true);
    setAnimationStep(0);
    setAnimationProgress(0);
    
    // Clear canvas first
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawGuideLines(ctx);
      }
    }
    
    // Start animation after a short delay to ensure state is updated
    setTimeout(() => {
      animateLetter();
    }, 100);
  };

  const animateLetter = () => {
    if (!currentLetter || !isAnimating) return;
    
    console.log('Animating letter:', currentLetter.letter, 'Step:', animationStep, 'Progress:', animationProgress);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawGuideLines(ctx);
    
    // Calculate center position for scaling
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = 1.5; // Scale factor for the SVG paths
    
    // Draw completed strokes
    ctx.save();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    for (let i = 0; i < animationStep; i++) {
      const strokePath = currentLetter.strokeOrder[i];
      if (strokePath) {
        ctx.beginPath();
        drawSVGPathScaled(ctx, strokePath, scale, centerX - 80, centerY - 80);
        ctx.stroke();
      }
    }
    
    // Draw current stroke with animation progress
    if (animationStep < currentLetter.strokeOrder.length) {
      const currentStrokePath = currentLetter.strokeOrder[animationStep];
      if (currentStrokePath) {
        // Create a path for the animated portion
        const animatedPath = getAnimatedPath(currentStrokePath, animationProgress);
        if (animatedPath.trim()) {
          ctx.beginPath();
          drawSVGPathScaled(ctx, animatedPath, scale, centerX - 80, centerY - 80);
          ctx.stroke();
        }
      }
    }
    
    ctx.restore();
    
    // Update animation progress
    setAnimationProgress(prev => {
      const newProgress = prev + 0.05; // Slower animation speed for better visibility
      if (newProgress >= 1) {
        // Move to next stroke
        setAnimationStep(prevStep => {
          const nextStep = prevStep + 1;
          if (nextStep >= currentLetter.strokeOrder.length) {
            // Animation complete, now animate dots
            setTimeout(() => {
              animateDots();
            }, 1000);
            return prevStep;
          }
          return nextStep;
        });
        return 0;
      }
      return newProgress;
    });
    
    // Continue animation
    if (animationStep < currentLetter.strokeOrder.length) {
      setTimeout(() => {
        if (isAnimating) {
          animateLetter();
        }
      }, 100); // Slower frame rate for better visibility
    }
  };

  const animateDots = () => {
    if (!currentLetter) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Calculate center position for scaling
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = 1.5;
    
    // Draw all completed strokes
    ctx.save();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    currentLetter.strokeOrder.forEach(strokePath => {
      ctx.beginPath();
      drawSVGPathScaled(ctx, strokePath, scale, centerX - 80, centerY - 80);
      ctx.stroke();
    });
    
    // Draw dots with animation
    ctx.fillStyle = '#3b82f6';
    const dots = getLetterDots(currentLetter.letter);
    
    dots.forEach((dot, index) => {
      setTimeout(() => {
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 6, 0, 2 * Math.PI);
        ctx.fill();
      }, index * 200);
    });
    
    ctx.restore();
    
    // Animation complete
    setTimeout(() => {
      setIsAnimating(false);
    }, dots.length * 200 + 500);
  };

  const getAnimatedPath = (pathData: string, progress: number): string => {
    // Parse SVG path and animate it based on progress
    const commands = pathData.split(/(?=[MLQ])/).filter(cmd => cmd.trim());
    let animatedPath = '';
    let currentProgress = 0;
    
    for (const command of commands) {
      if (command.trim()) {
        const parts = command.trim().split(/\s+/);
        const cmd = parts[0];
        
        if (cmd === 'M' && parts.length >= 3) {
          // Move command - show immediately
          animatedPath += `M ${parts[1]} ${parts[2]} `;
          currentProgress += 0.1;
        } else if (cmd === 'L' && parts.length >= 3) {
          // Line command - animate based on progress
          if (currentProgress < progress) {
            animatedPath += `L ${parts[1]} ${parts[2]} `;
          }
          currentProgress += 0.3;
        } else if (cmd === 'Q' && parts.length >= 5) {
          // Quadratic curve - animate based on progress
          if (currentProgress < progress) {
            animatedPath += `Q ${parts[1]} ${parts[2]} ${parts[3]} ${parts[4]} `;
          }
          currentProgress += 0.4;
        }
      }
    }
    
    return animatedPath;
  };

  const getLetterDots = (letter: string): { x: number; y: number }[] => {
    const canvas = canvasRef.current;
    if (!canvas) return [];
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Define dot positions for each letter
    const dotPositions: { [key: string]: { x: number; y: number }[] } = {
      'ب': [{ x: centerX + 30, y: centerY - 20 }],
      'پ': [{ x: centerX + 30, y: centerY - 20 }, { x: centerX + 40, y: centerY - 25 }],
      'ت': [{ x: centerX + 30, y: centerY - 20 }],
      'ث': [{ x: centerX + 30, y: centerY - 20 }, { x: centerX + 40, y: centerY - 25 }, { x: centerX + 50, y: centerY - 20 }],
      'ج': [{ x: centerX + 30, y: centerY - 20 }],
      'چ': [{ x: centerX + 30, y: centerY - 20 }, { x: centerX + 40, y: centerY - 25 }],
      'خ': [{ x: centerX + 30, y: centerY - 20 }],
      'ذ': [{ x: centerX + 30, y: centerY - 20 }],
      'ز': [{ x: centerX + 30, y: centerY - 20 }],
      'ژ': [{ x: centerX + 30, y: centerY - 20 }, { x: centerX + 40, y: centerY - 25 }],
      'ض': [{ x: centerX + 30, y: centerY - 20 }],
      'ظ': [{ x: centerX + 30, y: centerY - 20 }],
      'غ': [{ x: centerX + 30, y: centerY - 20 }],
      'ف': [{ x: centerX + 30, y: centerY - 20 }],
      'ق': [{ x: centerX + 30, y: centerY - 20 }],
      'ن': [{ x: centerX + 30, y: centerY - 20 }],
      'ی': [{ x: centerX + 30, y: centerY - 20 }]
    };
    
    return dotPositions[letter] || [];
  };

  const drawGuideLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentLetter || !showGuide) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    
    ctx.save();
    
    // Set up text styling for the letter guide
    ctx.fillStyle = '#d1d5db'; // Light grey color
    ctx.font = 'bold 120px Arial, sans-serif'; // Large, bold font
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = 0.4; // Semi-transparent
    
    // Center the letter in the canvas
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // Draw the Persian letter form as guide
    const letterForm = getLetterForm(currentForm);
    ctx.fillText(letterForm, centerX, centerY);
    
    ctx.restore();
  };

  const getLetterForm = (formType: 'isolated' | 'initial' | 'medial' | 'final'): string => {
    if (!currentLetter) return '';
    
    switch (formType) {
      case 'isolated':
        return currentLetter.letter;
      case 'initial':
        return getInitialForm(currentLetter.letter);
      case 'medial':
        return getMedialForm(currentLetter.letter);
      case 'final':
        return getFinalForm(currentLetter.letter);
      default:
        return currentLetter.letter;
    }
  };

  const getInitialForm = (letter: string): string => {
    // Return initial form with connecting line on the right
    const initialForms: { [key: string]: string } = {
      'آ': 'آ', 'ا': 'ا', 'ب': 'بـ', 'پ': 'پـ', 'ت': 'تـ', 'ث': 'ثـ', 'ج': 'جـ', 'چ': 'چـ',
      'ح': 'حـ', 'خ': 'خـ', 'د': 'د', 'ذ': 'ذ', 'ر': 'ر', 'ز': 'ز', 'ژ': 'ژ', 'س': 'سـ',
      'ش': 'شـ', 'ص': 'صـ', 'ض': 'ضـ', 'ط': 'طـ', 'ظ': 'ظـ', 'ع': 'عـ', 'غ': 'غـ', 'ف': 'فـ',
      'ق': 'قـ', 'ک': 'کـ', 'گ': 'گـ', 'ل': 'لـ', 'م': 'مـ', 'ن': 'نـ', 'و': 'و', 'ه': 'هـ',
      'ی': 'یـ'
    };
    return initialForms[letter] || letter;
  };

  const getMedialForm = (letter: string): string => {
    // Return medial form with connecting lines on both sides
    const medialForms: { [key: string]: string } = {
      'آ': 'آ', 'ا': 'ا', 'ب': 'ـبـ', 'پ': 'ـپـ', 'ت': 'ـتـ', 'ث': 'ـثـ', 'ج': 'ـجـ', 'چ': 'ـچـ',
      'ح': 'ـحـ', 'خ': 'ـخـ', 'د': 'د', 'ذ': 'ذ', 'ر': 'ر', 'ز': 'ز', 'ژ': 'ژ', 'س': 'ـسـ',
      'ش': 'ـشـ', 'ص': 'ـصـ', 'ض': 'ـضـ', 'ط': 'ـطـ', 'ظ': 'ـظـ', 'ع': 'ـعـ', 'غ': 'ـغـ', 'ف': 'ـفـ',
      'ق': 'ـقـ', 'ک': 'ـکـ', 'گ': 'ـگـ', 'ل': 'ـلـ', 'م': 'ـمـ', 'ن': 'ـنـ', 'و': 'و', 'ه': 'ـهـ',
      'ی': 'ـیـ'
    };
    return medialForms[letter] || letter;
  };

  const getFinalForm = (letter: string): string => {
    // Return final form with connecting line on the left
    const finalForms: { [key: string]: string } = {
      'آ': 'آ', 'ا': 'ا', 'ب': 'ـب', 'پ': 'ـپ', 'ت': 'ـت', 'ث': 'ـث', 'ج': 'ـج', 'چ': 'ـچ',
      'ح': 'ـح', 'خ': 'ـخ', 'د': 'د', 'ذ': 'ذ', 'ر': 'ر', 'ز': 'ز', 'ژ': 'ژ', 'س': 'ـس',
      'ش': 'ـش', 'ص': 'ـص', 'ض': 'ـض', 'ط': 'ـط', 'ظ': 'ـظ', 'ع': 'ـع', 'غ': 'ـغ', 'ف': 'ـف',
      'ق': 'ـق', 'ک': 'ـک', 'گ': 'ـگ', 'ل': 'ـل', 'م': 'ـم', 'ن': 'ـن', 'و': 'و', 'ه': 'ـه',
      'ی': 'ـی'
    };
    return finalForms[letter] || letter;
  };

  const drawSVGPathScaled = (ctx: CanvasRenderingContext2D, pathData: string, scale: number, offsetX: number, offsetY: number) => {
    // Simple SVG path parser for basic commands with scaling
    const commands = pathData.split(/(?=[MLQ])/).filter(cmd => cmd.trim());
    
    commands.forEach(command => {
      if (command.trim()) {
        const parts = command.trim().split(/\s+/);
        const type = parts[0];
        const coords = parts.slice(1).map(Number).filter(n => !isNaN(n));
        
        switch (type) {
          case 'M':
            if (coords.length >= 2) {
              ctx.moveTo(coords[0] * scale + offsetX, coords[1] * scale + offsetY);
            }
            break;
          case 'L':
            if (coords.length >= 2) {
              ctx.lineTo(coords[0] * scale + offsetX, coords[1] * scale + offsetY);
            }
            break;
          case 'Q':
            if (coords.length >= 4) {
              ctx.quadraticCurveTo(
                coords[0] * scale + offsetX, 
                coords[1] * scale + offsetY, 
                coords[2] * scale + offsetX, 
                coords[3] * scale + offsetY
              );
            }
            break;
        }
      }
    });
  };

  const drawSVGPath = (ctx: CanvasRenderingContext2D, pathData: string) => {
    // Simple SVG path parser for basic commands
    const commands = pathData.split(/(?=[MLQ])/).filter(cmd => cmd.trim());
    
    commands.forEach(command => {
      if (command.trim()) {
        const parts = command.trim().split(/\s+/);
        const type = parts[0];
        const coords = parts.slice(1).map(Number).filter(n => !isNaN(n));
        
        switch (type) {
          case 'M':
            if (coords.length >= 2) {
              ctx.moveTo(coords[0], coords[1]);
            }
            break;
          case 'L':
            if (coords.length >= 2) {
              ctx.lineTo(coords[0], coords[1]);
            }
            break;
          case 'Q':
            if (coords.length >= 4) {
              ctx.quadraticCurveTo(coords[0], coords[1], coords[2], coords[3]);
            }
            break;
        }
      }
    });
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!currentLetter || isCompleted) return;
    
    setIsDrawing(true);
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const newPoint: TracingPoint = {
      x,
      y,
      timestamp: Date.now()
    };
    
    setTracingPoints([newPoint]);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentLetter || isCompleted) return;
    
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const newPoint: TracingPoint = {
      x,
      y,
      timestamp: Date.now()
    };
    
    setTracingPoints(prev => [...prev, newPoint]);
    
    // Draw on canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx && tracingPoints.length > 0) {
        ctx.save();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        ctx.moveTo(tracingPoints[tracingPoints.length - 1].x, tracingPoints[tracingPoints.length - 1].y);
        ctx.lineTo(x, y);
        ctx.stroke();
        
        ctx.restore();
      }
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    
    // Only check for completion if user has drawn enough points
    if (tracingPoints.length >= 10) {
      calculateAccuracy();
    }
  };

  const calculateAccuracy = () => {
    if (!currentLetter || tracingPoints.length === 0) return;
    
    // Check if user's tracing matches the letter shape
    const isShapeMatched = checkShapeMatch();
    
    if (isShapeMatched) {
      // Calculate accuracy based on how well they traced
      const calculatedAccuracy = calculateShapeAccuracy();
      setAccuracy(calculatedAccuracy);
      setIsCompleted(true);
      onComplete?.(letter, calculatedAccuracy);
    } else {
      // Show feedback that they need to trace the letter
      setShowFeedback(true);
      setTimeout(() => {
        resetTracing();
      }, 2000);
    }
  };

  const checkShapeMatch = (): boolean => {
    if (!currentLetter || tracingPoints.length < 10) return false;
    
    const canvas = canvasRef.current;
    if (!canvas) return false;
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // Get the letter guide area (where the grey letter is displayed)
    const letterWidth = 120; // Approximate width of the letter
    const letterHeight = 120; // Approximate height of the letter
    const letterLeft = centerX - letterWidth / 2;
    const letterRight = centerX + letterWidth / 2;
    const letterTop = centerY - letterHeight / 2;
    const letterBottom = centerY + letterHeight / 2;
    
    // Count how many tracing points are within the letter area
    let pointsInLetterArea = 0;
    let totalPoints = tracingPoints.length;
    
    tracingPoints.forEach(point => {
      if (point.x >= letterLeft && point.x <= letterRight && 
          point.y >= letterTop && point.y <= letterBottom) {
        pointsInLetterArea++;
      }
    });
    
    // Require at least 70% of points to be within the letter area
    const coverageRatio = pointsInLetterArea / totalPoints;
    return coverageRatio >= 0.7;
  };

  const calculateShapeAccuracy = (): number => {
    if (!currentLetter || tracingPoints.length === 0) return 0;
    
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // Calculate how well the tracing covers the letter area
    const letterWidth = 120;
    const letterHeight = 120;
    const letterLeft = centerX - letterWidth / 2;
    const letterRight = centerX + letterWidth / 2;
    const letterTop = centerY - letterHeight / 2;
    const letterBottom = centerY + letterHeight / 2;
    
    let pointsInLetterArea = 0;
    tracingPoints.forEach(point => {
      if (point.x >= letterLeft && point.x <= letterRight && 
          point.y >= letterTop && point.y <= letterBottom) {
        pointsInLetterArea++;
      }
    });
    
    const coverageRatio = pointsInLetterArea / tracingPoints.length;
    
    // Base accuracy on coverage and stroke quality
    let accuracy = Math.round(coverageRatio * 100);
    
    // Bonus for good stroke length (not too short, not too long)
    const avgStrokeLength = calculateAverageStrokeLength();
    if (avgStrokeLength > 20 && avgStrokeLength < 200) {
      accuracy = Math.min(100, accuracy + 10);
    }
    
    return Math.max(60, accuracy); // Minimum 60% if they traced the letter
  };

  const calculateAverageStrokeLength = (): number => {
    if (tracingPoints.length < 2) return 0;
    
    let totalLength = 0;
    for (let i = 1; i < tracingPoints.length; i++) {
      const dx = tracingPoints[i].x - tracingPoints[i - 1].x;
      const dy = tracingPoints[i].y - tracingPoints[i - 1].y;
      totalLength += Math.sqrt(dx * dx + dy * dy);
    }
    
    return totalLength / (tracingPoints.length - 1);
  };

  const playLetterAudio = () => {
    if (currentLetter) {
      setIsPlaying(true);
      // Simulate audio playback
      setTimeout(() => setIsPlaying(false), 2000);
      // Call audio play callback if provided
      onAudioPlay?.(`/audio/letters/${currentLetter.letter}.mp3`);
    }
  };

  const nextLetter = () => {
    const currentIndex = persianLetterShapes.findIndex(shape => shape.letter === letter);
    if (currentIndex < persianLetterShapes.length - 1) {
      const nextLetterShape = persianLetterShapes[currentIndex + 1];
      setCurrentLetter(nextLetterShape);
      resetTracing();
    }
  };

  const previousLetter = () => {
    const currentIndex = persianLetterShapes.findIndex(shape => shape.letter === letter);
    if (currentIndex > 0) {
      const prevLetterShape = persianLetterShapes[currentIndex - 1];
      setCurrentLetter(prevLetterShape);
      resetTracing();
    }
  };

  if (!currentLetter) {
    return <div className="tracing-loading">Loading letter...</div>;
  }

  return (
    <div className="alphabet-tracing-container">
      {/* Header */}
      <div className="tracing-header">
        <div className="letter-info">
          <h2 className="letter-display">{currentLetter.letter}</h2>
          <div className="letter-details">
            <span className="letter-name">{currentLetter.name}</span>
            <span className="letter-pronunciation">({currentLetter.pronunciation})</span>
          </div>
        </div>
        
        <div className="tracing-controls">
          <button
            className="control-btn audio-btn"
            onClick={playLetterAudio}
            disabled={isPlaying}
          >
            {isPlaying ? <FaPause /> : <FaVolumeUp />}
          </button>
          
          <button
            className="control-btn reset-btn"
            onClick={resetTracing}
          >
            <FaUndo />
          </button>
          
          <button
            className="control-btn guide-btn"
            onClick={() => setShowGuide(!showGuide)}
          >
            {showGuide ? 'Hide Guide' : 'Show Guide'}
          </button>
          
          <button
            className="control-btn animation-btn"
            onClick={startAnimation}
            disabled={isAnimating}
          >
            {isAnimating ? <FaPause /> : <FaPlay />}
            {isAnimating ? 'Animating...' : 'Show Animation'}
          </button>
        </div>
        
        {/* Form Selector */}
        <div className="form-selector">
          <h4>Select Form to Trace:</h4>
          <div className="form-buttons">
            <button
              className={`form-btn ${currentForm === 'isolated' ? 'active' : ''}`}
              onClick={() => setCurrentForm('isolated')}
            >
              Isolated: {getLetterForm('isolated')}
            </button>
            <button
              className={`form-btn ${currentForm === 'initial' ? 'active' : ''}`}
              onClick={() => setCurrentForm('initial')}
            >
              Initial: {getLetterForm('initial')}
            </button>
            <button
              className={`form-btn ${currentForm === 'medial' ? 'active' : ''}`}
              onClick={() => setCurrentForm('medial')}
            >
              Medial: {getLetterForm('medial')}
            </button>
            <button
              className={`form-btn ${currentForm === 'final' ? 'active' : ''}`}
              onClick={() => setCurrentForm('final')}
            >
              Final: {getLetterForm('final')}
            </button>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="tracing-progress">
        <div className="progress-info">
          <span>Stroke {currentStroke + 1} of {currentLetter.strokeOrder.length}</span>
          <span className="difficulty-badge difficulty-{currentLetter.difficulty}">
            {currentLetter.difficulty}
          </span>
        </div>
        
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ 
              width: `${((currentStroke + (isCompleted ? 1 : 0)) / currentLetter.strokeOrder.length) * 100}%` 
            }}
          />
        </div>
      </div>

      {/* Tracing Canvas */}
      <div className="tracing-canvas-container">
        <canvas
          ref={canvasRef}
          width={400}
          height={400}
          className="tracing-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
        
        {isCompleted && (
          <div className="completion-overlay">
            <div className="completion-message">
              <FaCheck className="completion-icon" />
              <h3>Great job!</h3>
              <p>Accuracy: {accuracy}%</p>
              <div className="completion-actions">
                <button onClick={resetTracing} className="btn-secondary">
                  Try Again
                </button>
                <button onClick={nextLetter} className="btn-primary">
                  Next Letter
                </button>
              </div>
            </div>
          </div>
        )}
        
        {showFeedback && (
          <div className="completion-overlay">
            <div className="completion-message">
              <h3 style={{color: '#f59e0b'}}>Please trace the letter</h3>
              <p>Draw over the grey letter to complete the exercise</p>
            </div>
          </div>
        )}
      </div>

      {/* Letter Forms */}
      <div className="letter-forms">
        <h3>Letter Forms</h3>
        <div className="forms-grid">
          <div className="form-item">
            <span className="form-label">Isolated:</span>
            <span className="form-letter">{getLetterForm('isolated')}</span>
          </div>
          <div className="form-item">
            <span className="form-label">Initial:</span>
            <span className="form-letter">{getLetterForm('initial')}</span>
          </div>
          <div className="form-item">
            <span className="form-label">Medial:</span>
            <span className="form-letter">{getLetterForm('medial')}</span>
          </div>
          <div className="form-item">
            <span className="form-label">Final:</span>
            <span className="form-letter">{getLetterForm('final')}</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="tracing-navigation">
        <button
          className="nav-btn prev-btn"
          onClick={previousLetter}
          disabled={persianLetterShapes.findIndex(shape => shape.letter === letter) === 0}
        >
          Previous
        </button>
        
        <div className="letter-selector">
          <select
            value={letter}
            onChange={(e) => {
              const selectedLetter = persianLetterShapes.find(shape => shape.letter === e.target.value);
              if (selectedLetter) {
                setCurrentLetter(selectedLetter);
                resetTracing();
              }
            }}
          >
            {persianLetterShapes.map(shape => (
              <option key={shape.letter} value={shape.letter}>
                {shape.letter} - {shape.name}
              </option>
            ))}
          </select>
        </div>
        
        <button
          className="nav-btn next-btn"
          onClick={nextLetter}
          disabled={persianLetterShapes.findIndex(shape => shape.letter === letter) === persianLetterShapes.length - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AlphabetTracing;
